<template>
  <div class="page-container">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>{{ $t('horse.detailTitle') }}</span>
          <el-button @click="goBack" style="float: right;">
            {{ $t('common.back') }}
          </el-button>
        </div>
      </template>

      <el-descriptions :column="2" border v-loading="loading">
        <el-descriptions-item :label="$t('horse.name')">
          {{ horseData.name }}
        </el-descriptions-item>
        <el-descriptions-item :label="$t('horse.breed')">
          {{ horseData.breed }}
        </el-descriptions-item>
        <el-descriptions-item :label="$t('horse.color')">
          {{ horseData.color }}
        </el-descriptions-item>
        <el-descriptions-item :label="$t('horse.age')">
          {{ horseData.age }} {{ $t('horse.yearsOld') }}
        </el-descriptions-item>
        <el-descriptions-item :label="$t('horse.gender')">
          <el-tag :type="horseData.gender ? 'primary' : 'danger'">
            {{ horseData.gender ? '♂ Male' : '♀ Female' }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item :label="$t('horse.birthDate')">
          {{ horseData.birthDate ? new Date(horseData.birthDate).toLocaleDateString() : '-' }}
        </el-descriptions-item>
        <el-descriptions-item :label="$t('horse.avatar')" v-if="horseData.avatar">
          <ImagePreview 
            :src="horseData.avatar" 
            width="150px" 
            height="100px"
            :enable-preview="true"
          />
        </el-descriptions-item>
        <el-descriptions-item :label="$t('horse.remark')" :span="2">
          {{ horseData.remark || '-' }}
        </el-descriptions-item>
      </el-descriptions>

      <div class="mt-4">
        <el-button type="primary" @click="goToEdit">
          {{ $t('common.edit') }}
        </el-button>
      </div>
    </el-card>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { ElMessage } from 'element-plus'
import { getHorseDetail } from '@/api/horse'
import ImagePreview from '@/components/ImagePreview.vue'

const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const loading = ref(false)
const horseData = ref({})

const loadHorse = async () => {
  const id = route.params.id
  if (!id) return
  
  loading.value = true
  try {
    const res = await getHorseDetail(id)
    if (res.success && res.data) {
      horseData.value = res.data
    } else {
      ElMessage.error(res.message || 'Failed to load horse')
    }
  } catch (error) {
    ElMessage.error('Failed to load horse')
  } finally {
    loading.value = false
  }
}

const goBack = () => {
  router.push('/horse')
}

const goToEdit = () => {
  router.push(`/horse/edit/${route.params.id}`)
}

onMounted(() => {
  loadHorse()
})
</script>

<style scoped lang="scss">
.page-container {
  max-width: 1000px;
  margin: 0 auto;
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.mt-4 {
  margin-top: 16px;
}

// Mobile responsive styles
@media (max-width: 768px) {
  .page-container {
    padding: 12px;

    :deep(.el-card__header) {
      padding: 12px 16px;
    }

    :deep(.el-card__body) {
      padding: 16px;
    }
  }

  .card-header {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;

    .el-button {
      width: 100%;
      float: none !important;
    }
  }

  :deep(.el-descriptions) {
    :deep(.el-descriptions__body) {
      :deep(.el-descriptions__table) {
        display: block;

        tbody {
          display: block;
        }

        tr {
          display: flex;
          flex-direction: column;
          margin-bottom: 8px;
          border: 1px solid #ebeef5;
          border-radius: 4px;
          overflow: hidden;
        }

        td {
          display: block;
          width: 100% !important;
          border: none;
          border-bottom: 1px solid #ebeef5;

          &:last-child {
            border-bottom: none;
          }
        }
      }
    }

    :deep(.el-descriptions__label) {
      width: 100%;
      background-color: #f5f7fa;
      padding: 10px 12px;
      font-weight: 600;
    }

    :deep(.el-descriptions__content) {
      padding: 10px 12px;
    }
  }

  .mt-4 {
    margin-top: 20px;

    .el-button {
      width: 100%;
    }
  }
}

// Small mobile devices
@media (max-width: 480px) {
  .page-container {
    padding: 8px;

    :deep(.el-card__header) {
      padding: 10px 12px;

      .card-header {
        span {
          font-size: 16px;
        }
      }
    }

    :deep(.el-card__body) {
      padding: 12px;
    }
  }

  :deep(.el-descriptions) {
    :deep(.el-descriptions__label) {
      padding: 8px 10px;
      font-size: 13px;
    }

    :deep(.el-descriptions__content) {
      padding: 8px 10px;
      font-size: 13px;
    }
  }
}
</style>
